import React, { useState, useEffect, useRef, useCallback } from "react";
import s from "./ai-assistent.module.scss";
import NewChat from "./new-chat/new-chat";
import { ReactComponent as SendMessageIcon} from "assets/icons/send-message.svg";
import { ReactComponent as UserIcon} from "assets/icons/user.svg";
import { ReactComponent as ArrowIcon} from "assets/icons/angle-up.svg";


const AiAssistent = (props) => {
    const [session, setSession] = useState(props.session);
    const [isShowLeftMenu, setIsShowLeftMenu] = useState(props.showLeftMenu);
    const [expandedMessages, setExpandedMessages] = useState({});
    const [messageText, setMessageText] = useState("");
    const [showNewMessageIndicator, setShowNewMessageIndicator] = useState(false);
    const [isUserScrolledUp, setIsUserScrolledUp] = useState(false);

    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const prevMessagesLengthRef = useRef(0);
    const isUserScrolledUpRef = useRef(false);

const scrollToBottom = useCallback((behavior = 'smooth') => {
    const container = messagesContainerRef.current;
    if (container) {
        container.scrollTo({
            top: container.scrollHeight,
            behavior
        });
    }
    setShowNewMessageIndicator(false);
    setIsUserScrolledUp(false);
    isUserScrolledUpRef.current = false;
}, []);

    // Скролл вниз при открытии сессии
    useEffect(() => {
        setSession(props.session);
        setIsShowLeftMenu(props.showLeftMenu);

        if (props.session !== session) {
            setShowNewMessageIndicator(false);
            setIsUserScrolledUp(false);
            isUserScrolledUpRef.current = false;
            setTimeout(() => scrollToBottom('smooth'), 50);
        }
    }, [props]);

    useEffect(() => {
        const messages = props.messages || [];
        const prevLength = prevMessagesLengthRef.current;

        if (messages.length > prevLength && prevLength > 0) {
            const lastMessage = messages[messages.length - 1];
            const isFromBot = lastMessage?.role !== 'user';

            if (isFromBot && isUserScrolledUpRef.current) {
                setShowNewMessageIndicator(true);
            } else {
                scrollToBottom('smooth');
            }
        }

        prevMessagesLengthRef.current = messages.length;
    }, [props.messages, scrollToBottom]);

    const handleScroll = useCallback(() => {
        const container = messagesContainerRef.current;
        if (!container) return;

        const { scrollTop, scrollHeight, clientHeight } = container;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        const isScrolledUp = distanceFromBottom > 100;

        setIsUserScrolledUp(isScrolledUp);
        isUserScrolledUpRef.current = isScrolledUp;

        if (!isScrolledUp) {
            setShowNewMessageIndicator(false);
        }
    }, []);

    const getTwoLabelLang = () => {
        switch(props.selectedLanguage){
            case 'RUS': return 'ru';
            case 'ENG': return 'en';
            case 'KAZ': return 'kk';
        }
    };

    const sendMessage = (text = null) => {
        let t = text ? text : messageText;
        let payload = {
            company_id: props.companyId,
            user_id: props.accountId,
            user_name: props.login,
            message: t,
            //stream: true,
            target_language: getTwoLabelLang(),
            session_title: session && session.session_title ? session.session_title : null
        };
        setMessageText("");
        if (session && session.id) payload.session_id = session.id;

        // Scroll to bottom
        setIsUserScrolledUp(false);
        isUserScrolledUpRef.current = false;
        setShowNewMessageIndicator(false);
        setTimeout(() => scrollToBottom('smooth'), 50);

        props.sendMessageToAi(payload);
    };

    const handleInput = (e) => {
        const el = e.target;
        const charLimit = 100;
        const maxHeight = 100;

        if(props.errorNotified){
            props.setErrorNotified(null)
        }

        if (el.value.length <= charLimit) {
            el.style.overflowY = 'hidden';
        } else {
            el.style.height = 'auto';
            if (el.scrollHeight > maxHeight) {
                el.style.height = maxHeight + 'px';
                el.style.overflowY = 'auto';
            } else {
                el.style.height = el.scrollHeight + 'px';
                el.style.overflowY = 'hidden';
            }
        }

        setMessageText(el.value);
    };

    const { messages, isLoading, health, models, error } = props;
    const currentModel = models?.find(m => m.name === health?.model?.name);

    const checkDefault = (defaultText) => {
        sendMessage(defaultText);
    };

    const getHealthStatus = () => {
        if (!health) return null;
        const isHealthy = health.components?.llm_server?.status === "healthy";
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280' }}>
                <div style={{
                    width: '8px', height: '8px', marginLeft: '8px',
                    borderRadius: '50%',
                    background: isHealthy ? '#22C55E' : '#EAE460'
                }} />
            </div>
        );
    };

    const showOpenClose = () => {
        props.setShowLeftMenu(!isShowLeftMenu);
    };

    const toggleMessage = (key) => {
        setExpandedMessages(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const messagesBlock = (
        <div
            className={s.messages}
            ref={messagesContainerRef}
            onScroll={handleScroll}
        >
            {(messages || []).map((message, index) => {
                const key = message.id ?? index;
                const isExpanded = expandedMessages[key];
                const isLong = message.content?.length > 500;
                const displayContent = isLong && !isExpanded
                    ? message.content.slice(0, 500) + '...'
                    : message.content;

                return (
                    <div key={key} className={s.message}>
                        {message.role !== 'user' && (
                            <div className={s.userIcon} style={{ left: '-20px' }}>
                                <UserIcon />
                            </div>
                        )}
                        <div style={{
                            float: message.role === 'user' ? 'right' : 'left',
                            background: message.role === 'user' ? '#0EA5E9' : '#ffffff',
                            color: message.role === 'user' ? '#ffffff' : '#000000',
                            padding: '10px',
                            borderRadius: message.role === 'user' ? '8px 8px 2px 8px' : '2px 8px 8px 8px',
                            maxWidth: '70%',
                            whiteSpace: 'pre-wrap',
                            marginLeft: message.role !== 'user' ? '25px' : 0,
                            marginRight: message.role === 'user' ? '60px' : 0
                        }}>
                            {displayContent}
                            {isLong && (
                                <div onClick={() => toggleMessage(key)} style={{
                                    marginTop: '6px', cursor: 'pointer',
                                    fontSize: '12px',
                                    color: message.role === 'user' ? 'rgba(255,255,255,0.8)' : '#0EA5E9',
                                    userSelect: 'none'
                                }}>
                                    <span style={{
                                        fontSize: '14px',
                                        color: message.role === 'user' ? '#E0F2FE' : '#187be4',
                                        lineHeight: '220%',
                                        textDecoration: 'underline'
                                    }}>
                                        {isExpanded
                                            ? props.getValue('COLLAPSE') || 'Скрыть'
                                            : props.getValue('READ_MORE') || 'Читать далее'}
                                    </span>
                                </div>
                            )}
                        </div>
                        {message.role === 'user' && (
                            <div className={s.userIcon} style={{ right: '20px' }}>
                                <UserIcon />
                            </div>
                        )}
                    </div>
                );
            })}

        </div>
    );

    return (
        <div className={s.AiAssistent}>
            <div className={s.headerBlock}>
                <div className={s.header}>
                    <div style={{ width: '200px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        {(props.deviceType === 'mobile' || props.deviceType === 'tablet') && (
                            <button
                                className={`${s.openCloseMenu} ${isShowLeftMenu ? s.isOpened : s.isClosed}`}
                                onClick={showOpenClose}
                            >
                                <ArrowIcon />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {!session ? <NewChat getValue={props.getValue} checkDefault={checkDefault} /> : messagesBlock}

            {showNewMessageIndicator && isUserScrolledUp (
                <div className={s.scrollToBottom}>
                    <button
                    onClick={() => scrollToBottom('smooth')}
                >
                    <ArrowIcon />
                    </button>
                </div>
            )}

            <div></div>

            <div className={s.sendMessageBlock}>
                {isLoading ? (
                    <div className={s.preparingMessageBlock}>
                        <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" className={s.pulsingCircle}>
                            <circle cx="10" cy="10" r="10" fill="black" />
                        </svg>
                        <span className={s.preparingMesssage}>
                            {props.getValue('AI_ASSISSTENT_IS_PREPARING_A_RESPONSE')}
                        </span>
                    </div>
                ) : error ? (
                    props.errorNotified ? 
                        <div className={s.errorBlock}>
                            <span className={s.notificationMessage}>
                                {error ? props.getValue('ASSISTANT_NOTIFICATION') : null}
                            </span>
                        </div>
                    :
                        <div className={s.errorBlock}>
                            <span className={s.errorMessage}>
                                {error ? props.getValue('ASSISTANT_ERROR') : null}
                            </span>
                        </div>
                ) : null}

                <div className={s.textBlock}>
                    <textarea
                        type="text"
                        value={messageText}
                        onChange={handleInput}
                        rows={1}
                    />
                    <button
                        onClick={sendMessage.bind(this, null)}
                        className={s.sendMessageButton}
                        disabled={isLoading}
                    >
                        {isLoading
                            ? <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <rect width="19" height="19" fill="white" />
                              </svg>
                            : <SendMessageIcon />}
                    </button>
                </div>

                <div className={s.footerBlock}>
                    <span>{props.getValue('ECHO_AI_CAN_MAKE_MISTAKES')}</span>
                </div>
            </div>
        </div>
    );
};

export default AiAssistent;